import { fetchAnnotationImagePathApi } from '@/api/fetchAnnotationImagePath/fetchAnnotationImagePathApiGwFuncs'
import type {
  AnnotationImage,
  FetchAnnotationImageRequestParam
} from '@/types/api/fetchAnnotationImagePath/fetchAnnotationImagePathTypes'
import { defineStore } from 'pinia'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useS3Accessor } from '@/features/s3/useS3Accessor'
import MiiaFwConfig from '@/configs/config'

/**
 * APIの取得結果と画像実体のペア
 */
export interface AnnotationImageUrl {
  id: string
  annotationImage: AnnotationImage
  objectUrl: string
}

/**
 * State用型定義
 */
export interface AnnotationImageState {
  images: AnnotationImageUrl[]
}

/**
 * 初期state
 */
const initialState: AnnotationImageState = {
  images: []
}

/**
 * APIからアノテーション画像、且つS3から画像を取得する
 * @param params リクエストパラメータ
 * @returns 取得結果
 */
const fetchAnnotationImages = async (
  params: FetchAnnotationImageRequestParam
): Promise<AnnotationImageState> => {
  const { callFetchImage } = fetchAnnotationImagePathApi()
  // APIを実行
  const res = await callFetchImage(params)
  if (res.error !== undefined) {
    throw new GlobalError([ERROR_CODES.ANNOTATION_IMAGE_FETCH_FAILED])
  }
  // S3から画像を取得
  const result = res.result
  const images = await Promise.all(
    result.map(async a => {
      const path = `${params.govCode}/${params.disasterId}/${a.flightId}/Ai/${a.fileName}`
      const objectUrl = await getAnnotationImageUrl(path)
      return {
        id: a.annotationId,
        annotationImage: a,
        objectUrl: objectUrl
      }
    })
  )
  // 結果返却
  return { images: images }
}

/**
 * S3から画像を取得
 * @param path パス
 * @returns objectUrl
 */
const getAnnotationImageUrl = async (path: string): Promise<string> => {
  try {
    const { getFileFromS3 } = useS3Accessor()
    const file = await getFileFromS3(
      path,
      null,
      MiiaFwConfig.config.s3Access.contentsBucketScreeningGeoResult
    )
    return URL.createObjectURL(file)
  } catch (err) {
    console.error(err)
    throw new GlobalError([ERROR_CODES.ANNOTATION_IMAGE_FETCH_FAILED])
  }
}

// Pinia Store の定義
export const useAnnotationImageStore = defineStore('annotationImage', {
  state: (): AnnotationImageState => ({ ...initialState }),

  getters: {
    /**
     * stateを取得
     * @param state state
     * @returns state
     */
    getState: (state: AnnotationImageState): AnnotationImageState => {
      return state
    }
  },
  actions: {
    /**
     * State のすべての値を初期化
     */
    reset() {
      Object.assign(this.$state, initialState)
    },

    /**
     * APIからデータを取得
     * @param params APIリクエスト
     */
    async fetch(params: FetchAnnotationImageRequestParam): Promise<void> {
      const res = await fetchAnnotationImages(params)
      Object.assign(this.$state, res)
    }
  }
})
